import Searchbox from "components/fields/Searchbox";
import React from "react";
import {
  createColumnHelper, flexRender, getCoreRowModel,
  getExpandedRowModel, getSortedRowModel, SortingState, useReactTable,
} from "@tanstack/react-table";
import Pagination from "components/pagination";
import { BlackListClientsRowObj } from "..";
import DivLoader from "components/divloader/DivLoader";
import DeleteModal from "./DeleteBlockRecordModal";
import { deleteBlackListRecord } from "api/blackListClient";
import CreateBlockClientsModal from "./CreateBlockeclientModal";
import ViewHistoryModal from "./viewHistory";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import moment from "moment";
import toast from "react-hot-toast";

const columnHelper = createColumnHelper<BlackListClientsRowObj>();

function BlackList(props: {
  pageSize: any; setPageSize: any; page: any; setPage: any;
  totalpage: any; totalItems: any; currentPage: any; roleData: any;
  tableData: any; fetchBlackListClientsData: any;
  isLoading: boolean; setIsLoading: any; onValueChange?: any;
}) {
  const { pageSize, setPageSize, page, setPage, totalpage, totalItems, currentPage, roleData, tableData, fetchBlackListClientsData, isLoading, setIsLoading, onValueChange } = props;

  const [data, setData] = React.useState(() => [...(tableData ?? [])]);
  const [sorting, setSorting] = React.useState<SortingState>([]);

  React.useEffect(() => { setData(tableData ?? []); }, [tableData]);

  const handleDelete = (deleteId: string) => {
    setIsLoading(true);
    deleteBlackListRecord(deleteId)
      .then((data) => { fetchBlackListClientsData(); toast.success(data?.message || "Record deleted"); })
      .catch((err) => { toast.error(err?.response?.data?.message ?? "Record deletion failed"); })
      .finally(() => setIsLoading(false));
  };

  const hasActions = roleData?.[0]?.blacklist?.value?.edit_blacklist ||
    roleData?.[0]?.blacklist?.value?.view_blacklist ||
    roleData?.[0]?.blacklist?.value?.delete_blacklist;

  const columns = [
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => <span className="bl-th">Created At</span>,
      cell: (info) => (
        <div className="bl-date-cell">
          <div className="bl-date-icon">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <span className="bl-date">{moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss")}</span>
        </div>
      ),
      enableSorting: true,
    }),
    columnHelper.accessor("bin", {
      id: "last4",
      header: () => <span className="bl-th">BIN + Last 4</span>,
      sortingFn: (rowA, rowB) => {
        const a = `${rowA.original.bin || ""}${rowA.original.last_four || ""}`;
        const b = `${rowB.original.bin || ""}${rowB.original.last_four || ""}`;
        return a.localeCompare(b);
      },
      cell: (info) => {
        const bin = info.row.original.bin;
        const last4 = info.row.original.last_four;
        if (!bin && !last4) return <span className="bl-muted">—</span>;
        return (
          <div className="bl-card-cell">
            <div className="bl-card-icon">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
            </div>
            <span className="bl-card-num">{bin || ""}{last4 ? `–${last4}` : ""}</span>
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor("id", {
      id: "name",
      header: () => <span className="bl-th">Name</span>,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.first_name || "";
        const b = rowB.original.first_name || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
      cell: (info) => {
        const name = `${info.row.original.first_name || ""} ${info.row.original.last_name || ""}`.trim();
        if (!name) return <span className="bl-muted">—</span>;
        return (
          <div className="bl-name-cell">
            <div className="bl-avatar">{name.charAt(0).toUpperCase()}</div>
            <span className="bl-name">{name}</span>
          </div>
        );
      },
      enableSorting: true,
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => <span className="bl-th">Email</span>,
      cell: (info) => (
        <span className="bl-email">{info.getValue() || <span className="bl-muted">—</span>}</span>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.email || "";
        const b = rowB.original.email || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),
    columnHelper.accessor("attempts", {
      id: "attempts",
      header: () => <span className="bl-th">Attempts</span>,
      cell: (info) => {
        const val = info.getValue();
        if (!val) return <span className="bl-muted">—</span>;
        return <span className="bl-attempts-badge">{val}</span>;
      },
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.attempts || 0);
        const b = Number(rowB.original.attempts || 0);
        if (a !== b) {
          return a - b;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },

    }),
    ...(hasActions ? [columnHelper.accessor("id", {
      id: "actions",
      header: () => <span className="bl-th">Actions</span>,
      cell: (info) => (
        <div className="bl-actions">
          {roleData?.[0]?.blacklist?.value?.edit_blacklist && (
            <CreateBlockClientsModal
              fetchBlackListClientsData={fetchBlackListClientsData}
              id={info.getValue()}
              roleData={roleData}
              info={false}
              infoData={info}
            />
          )}
          <ViewHistoryModal
            fetchBlackListClientsData={fetchBlackListClientsData}
            id={info.getValue()}
            info={true}
            roleData={roleData}
            infoData={info}
            tableData={[]}
          />
          {roleData?.[0]?.blacklist?.value?.delete_blacklist && (
            <DeleteModal
              fetchListData={fetchBlackListClientsData}
              id={info.getValue()}
              name={info.row.original?.first_name ?? ""}
              heading="Delete Record"
              infoData={info}
              message=""
              handleDelete={handleDelete}
            />
          )}
        </div>
      ),
      enableSorting: false,
    })] : []),
  ];

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <div className="bl-card">
      {/* Header */}
      <div className="bl-header">
        <div className="bl-header-left">
          <div className="bl-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
            </svg>
          </div>
          <div>
            <h3 className="bl-title">Blacklist</h3>
            <p className="bl-subtitle">
              {totalItems > 0 ? `${totalItems} blocked record${totalItems !== 1 ? "s" : ""}` : "No records"}
            </p>
          </div>
        </div>
        <Searchbox onSearch={(e) => onValueChange(e)} />
      </div>

      {/* Table */}
      <div className="bl-table-wrap">
        {isLoading ? (
          <div className="bl-loading">
            <div className="bl-spinner" />
            <p className="bl-loading-txt">Loading blacklist…</p>
          </div>
        ) : (
          <table className="bl-table">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th
                      key={h.id}
                      colSpan={h.colSpan}
                      onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                      className={`bl-thead-th${h.column.getCanSort() ? " bl-thead-th--sort" : ""}`}
                    >
                      <div className="bl-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="bl-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> :
                             h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> :
                             <RiArrowUpDownFill size={11} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row, ri) => (
                  <tr key={row.id} className={`bl-tbody-tr${ri % 2 !== 0 ? " bl-tbody-tr--alt" : ""}`}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="bl-tbody-td">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7}>
                    <div className="bl-empty">
                      <div className="bl-empty-icon">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                        </svg>
                      </div>
                      <p className="bl-empty-title">No blacklisted records</p>
                      <p className="bl-empty-sub">Add blocked clients, cards or emails using the button above</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="bl-pagination-wrap">
        <Pagination setPage={setPage} page={page} totalpage={totalpage} currentPage={currentPage} pageSize={pageSize} setPageSize={setPageSize} arraySize={[50, 100, 200]} />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .bl-card { font-family:'Plus Jakarta Sans',sans-serif; background:#fff; border:1px solid #e2e8f0; border-radius:18px; overflow:hidden; box-shadow:0 2px 8px rgba(15,23,42,0.06); }

        .bl-header { padding:20px 24px 16px; border-bottom:1px solid #f1f5f9; display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; }
        .bl-header-left { display:flex; align-items:center; gap:12px; }
        .bl-header-icon { width:40px; height:40px; border-radius:11px; background:linear-gradient(135deg,#fff1f2,#ffe4e6); border:1.5px solid #fecdd3; display:flex; align-items:center; justify-content:center; color:#e11d48; flex-shrink:0; }
        .bl-title { font-size:15px; font-weight:700; color:#0f172a; letter-spacing:-0.2px; margin-bottom:2px; line-height:22px; }
        .bl-subtitle { font-size:12px; color:#94a3b8; font-weight:500; }

        .bl-table-wrap { overflow-x:auto; }
        .bl-table-wrap::-webkit-scrollbar { height:4px; }
        .bl-table-wrap::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }
        .bl-table { width:100%; border-collapse:collapse; }

        .bl-thead-th { padding:12px 16px 10px; background:#f8fafc; border-bottom:1.5px solid #e2e8f0; text-align:left; white-space:nowrap; user-select:none; }
        .bl-thead-th--sort { cursor:pointer; }
        .bl-thead-th--sort:hover { background:#f1f5f9; }
        .bl-th-inner { display:flex; align-items:center; gap:5px; }
        .bl-th { font-size:11.5px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:0.5px; }
        .bl-sort-icon { color:#94a3b8; display:flex; align-items:center; }
        .bl-thead-th--sort:hover .bl-sort-icon { color:#e11d48; }

        .bl-tbody-tr { transition:background .12s; }
        .bl-tbody-tr:hover { background:#fff7f7; }
        .bl-tbody-tr--alt { background:#fafbfc; }
        .bl-tbody-tr--alt:hover { background:#fff1f2; }
        .bl-tbody-td { padding:12px 16px; border-bottom:1px solid #f1f5f9; vertical-align:middle; }

        /* Cells */
        .bl-date-cell { display:flex; align-items:center; gap:8px; }
        .bl-date-icon { width:24px; height:24px; border-radius:6px; background:linear-gradient(135deg,#fff1f2,#ffe4e6); border:1.5px solid #fecdd3; display:flex; align-items:center; justify-content:center; color:#e11d48; flex-shrink:0; }
        .bl-date { font-size:12.5px; font-weight:600; color:#0f172a; white-space:nowrap; }

        .bl-card-cell { display:flex; align-items:center; gap:7px; }
        .bl-card-icon { width:24px; height:24px; border-radius:6px; background:#f8fafc; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#64748b; flex-shrink:0; }
        .bl-card-num { font-size:12.5px; font-weight:700; color:#0f172a; letter-spacing:0.5px; font-family:'Fira Code',monospace; }

        .bl-name-cell { display:flex; align-items:center; gap:8px; }
        .bl-avatar { width:28px; height:28px; border-radius:7px; flex-shrink:0; background:linear-gradient(135deg,#fff1f2,#ffe4e6); border:1.5px solid #fecdd3; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:800; color:#e11d48; }
        .bl-name { font-size:13px; font-weight:600; color:#0f172a; }
        .bl-email { font-size:12.5px; font-weight:500; color:#475569; }
        .bl-muted { font-size:12.5px; color:#cbd5e1; font-weight:500; }

        .bl-attempts-badge { display:inline-flex; align-items:center; padding:3px 10px; border-radius:7px; font-size:12px; font-weight:700; background:linear-gradient(135deg,#fff7ed,#ffedd5); border:1.5px solid #fed7aa; color:#c2410c; }

        .bl-actions { display:flex; align-items:center; gap:6px; }

        .bl-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px 24px; gap:10px; }
        .bl-empty-icon { width:52px; height:52px; border-radius:13px; background:#f8fafc; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#cbd5e1; }
        .bl-empty-title { font-size:14px; font-weight:700; color:#64748b; }
        .bl-empty-sub { font-size:12.5px; color:#94a3b8; font-weight:500; text-align:center; }

        .bl-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px; gap:12px; }
        .bl-spinner { width:28px; height:28px; border:3px solid #e2e8f0; border-top-color:#e11d48; border-radius:50%; animation:blSpin .7s linear infinite; }
        @keyframes blSpin { to { transform:rotate(360deg); } }
        .bl-loading-txt { font-size:13px; color:#94a3b8; font-weight:500; }

        .bl-pagination-wrap { padding:12px 16px; border-top:1px solid #f1f5f9; background:#fafbfc; }

        @media (max-width:700px) { .bl-header { flex-direction:column; align-items:flex-start; } }
      `}</style>
    </div>
  );
}

export default BlackList;